// ============================================================
// talentTree.js — Path of Exile–style radial talent tree
// ~622 nodes across 10 branches + bridge nodes
// ============================================================

const allNodes = [];
const allEdges = [];

// ── Global node spacing scale (1.25 = 25% more spread out) ───
const NODE_SCALE = 1.25;

// ── helpers ──────────────────────────────────────────────────
function addEdge(a, b) {
  allEdges.push([a, b]);
  const nA = allNodes.find(n => n.id === a);
  const nB = allNodes.find(n => n.id === b);
  if (nA && !nA.connections.includes(b)) nA.connections.push(b);
  if (nB && !nB.connections.includes(a)) nB.connections.push(a);
}

function mk(nodeId, r, a, type, branch, color, def) {
  return {
    id: nodeId,
    x: Math.round(Math.cos(a) * r * NODE_SCALE),
    y: Math.round(Math.sin(a) * r * NODE_SCALE),
    type,
    branch,
    color,
    connections: [],
    ...def,
  };
}

// ── Origin node ───────────────────────────────────────────────
allNodes.push({
  id: 'origin',
  x: 0,
  y: 0,
  type: 'origin',
  label: 'Survivor',
  icon: '🌟',
  desc: 'The beginning of all paths',
  branch: 'origin',
  color: '#ffd700',
  effect: (s) => {},
  connections: [],
});

// ── Branch builder ────────────────────────────────────────────
function buildBranch(branch) {
  const { id, angle, color, smalls, hub, notables, keystones } = branch;

  // Support both legacy single-object `small` and new array `smalls`
  const getSmall = (index) => Array.isArray(smalls) ? smalls[index % smalls.length] : smalls;

  const mkNode = (nodeId, r, a, type, def) =>
    mk(nodeId, r, a, type, id, color, def);

  // Spine — cycle through smalls by spine position
  const spineIds = [0, 1, 2, 3, 4].map(i => `${id}_sp${i}`);
  [80, 140, 200, 265, 330].forEach((r, i) =>
    allNodes.push(mkNode(spineIds[i], r, angle, 'small', getSmall(i)))
  );
  addEdge('origin', spineIds[0]);
  for (let i = 0; i < 4; i++) addEdge(spineIds[i], spineIds[i + 1]);

  // Hub notable
  const hubId = `${id}_hub`;
  allNodes.push(mkNode(hubId, 390, angle, 'notable', hub));
  addEdge(spineIds[4], hubId);

  // 4 sub-branches — each sub-branch starts at a different offset in the smalls rotation
  const subOffsets = [-0.38, -0.13, 0.13, 0.38];
  const notableIds = [];
  subOffsets.forEach((da, s) => {
    const sa = angle + da;

    const subSmallIds = [0, 1, 2, 3, 4, 5, 6, 7].map(j => `${id}_p${s}_${j}`);
    [440, 478, 516, 554, 592, 630, 668, 706].forEach((r, j) =>
      allNodes.push(mkNode(subSmallIds[j], r, sa, 'small', getSmall(s + j)))
    );
    addEdge(hubId, subSmallIds[0]);
    for (let j = 0; j < 7; j++) addEdge(subSmallIds[j], subSmallIds[j + 1]);

    const notable = notables[s % notables.length];
    const notId = `${id}_p${s}_n`;
    notableIds.push(notId);
    allNodes.push(mkNode(notId, 748, sa, 'notable', notable));
    addEdge(subSmallIds[7], notId);

    const tailIds = [0, 1, 2].map(j => `${id}_p${s}_t${j}`);
    [790, 830, 870].forEach((r, j) =>
      allNodes.push(mkNode(tailIds[j], r, sa, 'small', getSmall(s + j + 5)))
    );
    addEdge(notId, tailIds[0]);
    addEdge(tailIds[0], tailIds[1]);
    addEdge(tailIds[1], tailIds[2]);

    const ks = keystones[s % keystones.length];
    const ksId = `${id}_p${s}_ks`;
    allNodes.push(mkNode(ksId, 930, sa, 'keystone', ks));
    addEdge(tailIds[2], ksId);
  });

  // ── Extra intra-branch connections ──────────────────────────────
  // Notable nodes connect laterally to adjacent notables within the branch
  for (let s = 0; s < 3; s++) addEdge(notableIds[s], notableIds[s + 1]);
  // Each notable also connects to the mid small node (index 4) of the neighboring sub-branch,
  // giving players extra routing options to cross between sub-branches
  for (let s = 0; s < 4; s++) {
    const next = (s + 1) % 4;
    addEdge(notableIds[s], `${id}_p${next}_4`);
  }
}

// ── Branch definitions ────────────────────────────────────────

const branches = [
  // ── Branch 1: Warfare ──────────────────────────────────────
  {
    id: 'war',
    label: 'Warfare',
    color: '#e05252',
    angle: 0,
    smalls: [
      { icon: '⚔️', label: 'Edge Training',    desc: '+0.5 Attack Damage',  effect: s => { s.attackDamage += 0.5; } },
      { icon: '📏', label: 'Extended Reach',   desc: '+2% Attack Range',     effect: s => { s.rangeMult *= 1.02; } },
      { icon: '🏃', label: 'Combat Footwork',  desc: '+2% Move Speed',       effect: s => { s.speedMult *= 1.02; } },
      { icon: '⚡', label: 'Battle Tempo',     desc: '-1% Spell Cooldown',   effect: s => { s.cdMult *= 0.99; } },
    ],
    hub: {
      icon: '⚔️',
      label: 'Blade Mastery',
      desc: '+2 ATK, +10% Range',
      effect: s => { s.attackDamage += 2; s.rangeMult *= 1.10; },
    },
    notables: [
      {
        icon: '🗡️',
        label: 'Combat Training',
        desc: '+2 ATK, +5% Speed',
        effect: s => { s.attackDamage += 2; s.speedMult *= 1.05; },
      },
      {
        icon: '💢',
        label: "Berserker's Grip",
        desc: '+3 ATK, -10% Cooldown',
        effect: s => { s.attackDamage += 3; s.cdMult *= 0.90; },
      },
      {
        icon: '🔱',
        label: "Warlord's Will",
        desc: '+2 ATK, +15% Range',
        effect: s => { s.attackDamage += 2; s.rangeMult *= 1.15; },
      },
      {
        icon: '🛡️',
        label: 'Steel Resolve',
        desc: '+3 ATK, +20 Max HP',
        effect: s => { s.attackDamage += 3; s.maxHp += 20; s.hp = Math.min(s.maxHp, s.hp + 20); },
      },
    ],
    keystones: [
      {
        icon: '⚔️',
        label: 'God of War',
        desc: '+8 Attack Damage',
        effect: s => { s.attackDamage += 8; },
      },
      {
        icon: '💥',
        label: 'Execution',
        desc: '+5 ATK, +20% Range, -15% Cooldown',
        effect: s => { s.attackDamage += 5; s.rangeMult *= 1.20; s.cdMult *= 0.85; },
      },
      {
        icon: '⚡',
        label: 'Berserker Ascendant',
        desc: '+6 ATK, +20% Speed',
        effect: s => { s.attackDamage += 6; s.speedMult *= 1.20; },
      },
      {
        icon: '🔥',
        label: 'Fury Incarnate',
        desc: '+7 ATK, ×1.2 Damage Multiplier',
        effect: s => { s.attackDamage += 7; s.damageMult *= 1.20; },
      },
    ],
  },

  // ── Branch 2: Critical Strike ─────────────────────────────
  {
    id: 'crit',
    label: 'Critical Strike',
    color: '#ffe566',
    angle: Math.PI / 8,         // 22.5° — between Warfare (0°) and Swiftness (45°)
    smalls: [
      { icon: '🎯', label: 'Sharp Eye',       desc: '+1% Crit Chance',      effect: s => { s.critRate = Math.min(0.80, (s.critRate ?? 0.05) + 0.01); } },
      { icon: '🗡️', label: 'Honed Edge',      desc: '+0.05× Crit Damage',   effect: s => { s.critDamage = (s.critDamage ?? 1.5) + 0.05; } },
      { icon: '⚔️', label: 'Strike Training', desc: '+0.3 Attack Damage',   effect: s => { s.attackDamage += 0.3; } },
      { icon: '⚡', label: 'Hair Trigger',    desc: '-1% Cooldown',         effect: s => { s.cdMult *= 0.99; } },
    ],
    hub: {
      icon: '💥',
      label: 'Keen Instincts',
      desc: '+8% Crit Chance, +0.1× Crit Damage',
      effect: s => { s.critRate = Math.min(0.80, (s.critRate ?? 0.05) + 0.08); s.critDamage = (s.critDamage ?? 1.5) + 0.1; },
    },
    notables: [
      {
        icon: '🎯',
        label: 'Precision Strikes',
        desc: '+5% Crit Chance, +0.25× Crit Dmg',
        effect: s => { s.critRate = Math.min(0.80, s.critRate + 0.05); s.critDamage += 0.25; },
      },
      {
        icon: '⚡',
        label: 'Lethal Tempo',
        desc: '+8% Crit Chance, -10% Cooldown',
        effect: s => { s.critRate = Math.min(0.80, s.critRate + 0.08); s.cdMult *= 0.90; },
      },
      {
        icon: '💢',
        label: 'Glass Cannon',
        desc: '+6% Crit Chance, +0.4× Crit Dmg',
        effect: s => { s.critRate = Math.min(0.80, s.critRate + 0.06); s.critDamage += 0.40; },
      },
      {
        icon: '🔪',
        label: "Assassin's Edge",
        desc: '+10% Crit Chance, +2 ATK',
        effect: s => { s.critRate = Math.min(0.80, s.critRate + 0.10); s.attackDamage += 2; },
      },
    ],
    keystones: [
      {
        icon: '💥',
        label: 'Death Mark',
        desc: '+20% Crit Chance, +0.5× Crit Dmg',
        effect: s => { s.critRate = Math.min(0.80, s.critRate + 0.20); s.critDamage += 0.5; },
      },
      {
        icon: '🎯',
        label: 'Perfect Aim',
        desc: '+30% Crit Chance',
        effect: s => { s.critRate = Math.min(0.80, s.critRate + 0.30); },
      },
      {
        icon: '⚔️',
        label: "Executioner's Blade",
        desc: '+15% Crit Chance, +1.0× Crit Dmg',
        effect: s => { s.critRate = Math.min(0.80, s.critRate + 0.15); s.critDamage += 1.0; },
      },
      {
        icon: '🔥',
        label: 'Savage Strikes',
        desc: '+10% Crit Chance, +5 ATK',
        effect: s => { s.critRate = Math.min(0.80, s.critRate + 0.10); s.attackDamage += 5; },
      },
    ],
  },

  // ── Branch 3: Swiftness ────────────────────────────────────
  {
    id: 'swift',
    label: 'Swiftness',
    color: '#52c8e0',
    angle: Math.PI / 4,
    smalls: [
      { icon: '⚡', label: 'Agility Training', desc: '+4% Move Speed',       effect: s => { s.speedMult *= 1.04; } },
      { icon: '📏', label: 'Fluid Motion',     desc: '+2% Attack Range',     effect: s => { s.rangeMult *= 1.02; } },
      { icon: '⏱️', label: 'Quick Draw',       desc: '-1.5% Cooldown',      effect: s => { s.cdMult *= 0.985; } },
      { icon: '🎯', label: 'On the Move',      desc: '+0.5% Crit Chance',   effect: s => { s.critRate = Math.min(0.80, (s.critRate ?? 0.05) + 0.005); } },
    ],
    hub: {
      icon: '💨',
      label: 'Fleet Footed',
      desc: '+15% Move Speed',
      effect: s => { s.speedMult *= 1.15; },
    },
    notables: [
      {
        icon: '🌪️',
        label: 'Wind Dancer',
        desc: '+15% Speed, -10% Cooldown',
        effect: s => { s.speedMult *= 1.15; s.cdMult *= 0.90; },
      },
      {
        icon: '👁️',
        label: 'Blur',
        desc: '+20% Speed',
        effect: s => { s.speedMult *= 1.20; },
      },
      {
        icon: '🏃',
        label: 'Shadow Run',
        desc: '+15% Speed, +15% Range',
        effect: s => { s.speedMult *= 1.15; s.rangeMult *= 1.15; },
      },
      {
        icon: '✨',
        label: 'Gale Force',
        desc: '+20% Speed, +2 XP/Kill',
        effect: s => { s.speedMult *= 1.20; s.xpPerKill += 2; },
      },
    ],
    keystones: [
      {
        icon: '⚡',
        label: 'Overdrive',
        desc: '+40% Move Speed',
        effect: s => { s.speedMult *= 1.40; },
      },
      {
        icon: '💫',
        label: 'Phase Form',
        desc: '+30% Speed, -25% Cooldown',
        effect: s => { s.speedMult *= 1.30; s.cdMult *= 0.75; },
      },
      {
        icon: '🌊',
        label: 'Untouchable',
        desc: '+25% Speed, -400ms Hit Cooldown',
        effect: s => { s.speedMult *= 1.25; s.hitCooldown = Math.max(500, (s.hitCooldown ?? 1500) - 400); },
      },
      {
        icon: '⚡',
        label: 'Lightning Legs',
        desc: '+35% Speed, +10% Range',
        effect: s => { s.speedMult *= 1.35; s.rangeMult *= 1.10; },
      },
    ],
  },

  // ── Branch 3: Sorcery ──────────────────────────────────────
  {
    id: 'sorc',
    label: 'Sorcery',
    color: '#a552e0',
    angle: Math.PI / 2,
    smalls: [
      { icon: '🔮', label: 'Arcane Training', desc: '-4% Spell Cooldown',  effect: s => { s.cdMult *= 0.96; } },
      { icon: '🏃', label: 'Mana Flow',        desc: '+3% Move Speed',      effect: s => { s.speedMult *= 1.03; } },
      { icon: '📏', label: 'Arcane Reach',     desc: '+2% Spell Range',     effect: s => { s.rangeMult *= 1.02; } },
      { icon: '⚔️', label: 'Spell Surge',      desc: '+0.3 Attack Damage',  effect: s => { s.attackDamage += 0.3; } },
    ],
    hub: {
      icon: '🌟',
      label: 'Arcane Mastery',
      desc: '-15% Cooldown',
      effect: s => { s.cdMult *= 0.85; },
    },
    notables: [
      {
        icon: '🔮',
        label: 'Spellweaver',
        desc: '-15% Cooldown, +5% Range',
        effect: s => { s.cdMult *= 0.85; s.rangeMult *= 1.05; },
      },
      {
        icon: '⚡',
        label: 'Mana Surge',
        desc: '-20% Cooldown',
        effect: s => { s.cdMult *= 0.80; },
      },
      {
        icon: '🌀',
        label: 'Focused Mind',
        desc: '-15% Cooldown, +3 ATK',
        effect: s => { s.cdMult *= 0.85; s.attackDamage += 3; },
      },
      {
        icon: '💫',
        label: 'Arcane Power',
        desc: '-20% Cooldown, +2 XP/Kill',
        effect: s => { s.cdMult *= 0.80; s.xpPerKill += 2; },
      },
    ],
    keystones: [
      {
        icon: '🔮',
        label: 'Archmage',
        desc: '-50% Cooldown',
        effect: s => { s.cdMult *= 0.50; },
      },
      {
        icon: '⚡',
        label: 'Spellstorm',
        desc: '-35% Cooldown, +3 ATK, +10% Range',
        effect: s => { s.cdMult *= 0.65; s.attackDamage += 3; s.rangeMult *= 1.10; },
      },
      {
        icon: '🌟',
        label: 'Grand Sorcerer',
        desc: '-40% Cooldown, +4 XP/Kill',
        effect: s => { s.cdMult *= 0.60; s.xpPerKill += 4; },
      },
      {
        icon: '💫',
        label: 'Omniscience',
        desc: '-30% Cooldown, +20% Speed, +2 ATK',
        effect: s => { s.cdMult *= 0.70; s.speedMult *= 1.20; s.attackDamage += 2; },
      },
    ],
  },

  // ── Branch 4: Fortune ──────────────────────────────────────
  {
    id: 'fort',
    label: 'Fortune',
    color: '#e0c052',
    angle: 3 * Math.PI / 4,
    smalls: [
      { icon: '💰', label: 'Lucky Eye',     desc: '+6% Gold Find',         effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 0.06; } },
      { icon: '✨', label: 'Keen Senses',  desc: '+2% Magic Find',         effect: s => { s.magicFind = (s.magicFind ?? 0) + 0.02; } },
      { icon: '📖', label: 'Lore Study',   desc: '+0.2 XP/Kill',           effect: s => { s.xpPerKill = (s.xpPerKill ?? 1) + 0.2; } },
      { icon: '🎯', label: 'Fortune Aim',  desc: '+0.5% Crit Chance',     effect: s => { s.critRate = Math.min(0.80, (s.critRate ?? 0.05) + 0.005); } },
    ],
    hub: {
      icon: '💎',
      label: 'Lucky Find',
      desc: '+25% Gold Find',
      effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 0.25; },
    },
    notables: [
      {
        icon: '🪙',
        label: "Merchant's Eye",
        desc: '+25% Gold Find, +10% Magic Find',
        effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 0.25; s.magicFind = (s.magicFind ?? 0) + 0.10; },
      },
      {
        icon: '💰',
        label: 'Treasure Hunter',
        desc: '+30% Gold Find, +15% Magic Find',
        effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 0.30; s.magicFind = (s.magicFind ?? 0) + 0.15; },
      },
      {
        icon: '✨',
        label: 'Golden Touch',
        desc: '+25% Gold Find, +15% Magic Find, +1 XP/Kill',
        effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 0.25; s.magicFind = (s.magicFind ?? 0) + 0.15; s.xpPerKill += 1; },
      },
      {
        icon: '🏆',
        label: "Hoarder's Instinct",
        desc: '+40% Gold Find, +20% Magic Find',
        effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 0.40; s.magicFind = (s.magicFind ?? 0) + 0.20; },
      },
    ],
    keystones: [
      {
        icon: '💰',
        label: 'Gold Rush',
        desc: '+100% Gold Find, +30% Magic Find',
        effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 1.00; s.magicFind = (s.magicFind ?? 0) + 0.30; },
      },
      {
        icon: '🤑',
        label: 'Plunder King',
        desc: '+75% Gold Find, +20% Magic Find, +2 ATK',
        effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 0.75; s.magicFind = (s.magicFind ?? 0) + 0.20; s.attackDamage += 2; },
      },
      {
        icon: '💎',
        label: "Midas' Blessing",
        desc: '+80% Gold Find, +25% Magic Find, +10% Speed',
        effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 0.80; s.magicFind = (s.magicFind ?? 0) + 0.25; s.speedMult *= 1.10; },
      },
      {
        icon: '🪙',
        label: "Fortune's Favorite",
        desc: '+90% Gold Find, +25% Magic Find, -10% Cooldown',
        effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 0.90; s.magicFind = (s.magicFind ?? 0) + 0.25; s.cdMult *= 0.90; },
      },
    ],
  },

  // ── Branch 5: Vitality ─────────────────────────────────────
  {
    id: 'vit',
    label: 'Vitality',
    color: '#52e084',
    angle: Math.PI,
    smalls: [
      { icon: '🛡️', label: 'Toughening',     desc: '-40ms Hit Cooldown',      effect: s => { s.hitCooldown = Math.max(600, (s.hitCooldown ?? 1500) - 40); } },
      { icon: '❤️', label: 'Life Gain',       desc: '+8 Max HP',               effect: s => { s.maxHp = (s.maxHp ?? 100) + 8; s.hp = Math.min(s.maxHp, s.hp + 8); } },
      { icon: '⚒️', label: 'Body Hardening',  desc: '+1 Armor',                effect: s => { s.armor = (s.armor ?? 0) + 1; } },
      { icon: '🩺', label: 'Resistance',      desc: '+2% Physical Resistance', effect: s => { s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.02); } },
    ],
    hub: {
      icon: '❤️',
      label: 'Iron Will',
      desc: '+40 Max HP',
      effect: s => { s.maxHp = (s.maxHp ?? 100) + 40; s.hp = Math.min(s.maxHp, s.hp + 40); },
    },
    notables: [
      {
        icon: '💪',
        label: 'Fortitude',
        desc: '+20 Max HP, -100ms Hit Cooldown',
        effect: s => { s.maxHp += 20; s.hp = Math.min(s.maxHp, s.hp + 20); s.hitCooldown = Math.max(600, (s.hitCooldown ?? 1500) - 100); },
      },
      {
        icon: '🛡️',
        label: 'Last Stand',
        desc: '+40 Max HP',
        effect: s => { s.maxHp += 40; s.hp = Math.min(s.maxHp, s.hp + 40); },
      },
      {
        icon: '❤️',
        label: 'Resilience',
        desc: '+20 Max HP, +15% Speed, +5% Physical Resistance',
        effect: s => { s.maxHp += 20; s.hp = Math.min(s.maxHp, s.hp + 20); s.speedMult *= 1.15; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.05); },
      },
      {
        icon: '🩸',
        label: 'Endurance',
        desc: '+60 Max HP',
        effect: s => { s.maxHp += 60; s.hp = Math.min(s.maxHp, s.hp + 60); },
      },
    ],
    keystones: [
      {
        icon: '❤️',
        label: "Titan's Endurance",
        desc: '+100 Max HP',
        effect: s => { s.maxHp += 100; s.hp = Math.min(s.maxHp, s.hp + 100); },
      },
      {
        icon: '🛡️',
        label: 'Deathless',
        desc: '+60 Max HP, -400ms Hit Cooldown',
        effect: s => { s.maxHp += 60; s.hp = Math.min(s.maxHp, s.hp + 60); s.hitCooldown = Math.max(600, (s.hitCooldown ?? 1500) - 400); },
      },
      {
        icon: '💀',
        label: 'Immortal Core',
        desc: '+80 Max HP, -200ms Hit Cooldown',
        effect: s => { s.maxHp += 80; s.hp = Math.min(s.maxHp, s.hp + 80); s.hitCooldown = Math.max(600, (s.hitCooldown ?? 1500) - 200); },
      },
      {
        icon: '🩸',
        label: 'Undying Resolve',
        desc: '+80 Max HP, +20% Speed',
        effect: s => { s.maxHp += 80; s.hp = Math.min(s.maxHp, s.hp + 80); s.speedMult *= 1.20; },
      },
    ],
  },

  // ── Branch 6: Fortification (Armor + Physical Resistance) ─
  {
    id: 'armor',
    label: 'Fortification',
    color: '#80c8e8',
    angle: 9 * Math.PI / 8,     // 202.5° — between Vitality (180°) and Wisdom (225°)
    smalls: [
      { icon: '🛡️', label: 'Plate Training',   desc: '+1 Armor',                effect: s => { s.armor = (s.armor ?? 0) + 1; } },
      { icon: '🩺', label: 'Hardening',         desc: '+3% Physical Resistance', effect: s => { s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.03); } },
      { icon: '❤️', label: 'Reinforced Body',   desc: '+6 Max HP',               effect: s => { s.maxHp = (s.maxHp ?? 100) + 6; s.hp = Math.min(s.maxHp, s.hp + 6); } },
      { icon: '🛡️', label: 'Quick Buckle',      desc: '-30ms Hit Cooldown',      effect: s => { s.hitCooldown = Math.max(600, (s.hitCooldown ?? 1500) - 30); } },
    ],
    hub: {
      icon: '⚔️',
      label: 'Iron Plating',
      desc: '+6 Armor, +5% Physical Resistance',
      effect: s => { s.armor = (s.armor ?? 0) + 6; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.05); },
    },
    notables: [
      {
        icon: '🛡️',
        label: 'Reinforced Mail',
        desc: '+4 Armor, +20 Max HP, +5% Physical Resistance',
        effect: s => { s.armor += 4; s.maxHp += 20; s.hp = Math.min(s.maxHp, s.hp + 20); s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.05); },
      },
      {
        icon: '🏰',
        label: 'Bulwark',
        desc: '+6 Armor, +8% Physical Resistance',
        effect: s => { s.armor += 6; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.08); },
      },
      {
        icon: '⚒️',
        label: 'Tempered Steel',
        desc: '+5 Armor, +2 ATK, +5% Physical Resistance',
        effect: s => { s.armor += 5; s.attackDamage += 2; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.05); },
      },
      {
        icon: '💎',
        label: 'Diamond Skin',
        desc: '+8 Armor, +8% Physical Resistance',
        effect: s => { s.armor += 8; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.08); },
      },
    ],
    keystones: [
      {
        icon: '🏰',
        label: 'Living Fortress',
        desc: '+18 Armor, +10% Physical Resistance',
        effect: s => { s.armor += 18; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.10); },
      },
      {
        icon: '⚔️',
        label: 'Sword and Board',
        desc: '+12 Armor, +4 ATK, +8% Physical Resistance',
        effect: s => { s.armor += 12; s.attackDamage += 4; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.08); },
      },
      {
        icon: '🛡️',
        label: 'Impenetrable',
        desc: '+15 Armor, +15% Physical Resistance, -300ms Hit Cooldown',
        effect: s => { s.armor += 15; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.15); s.hitCooldown = Math.max(600, (s.hitCooldown ?? 1500) - 300); },
      },
      {
        icon: '💪',
        label: "Warlord's Armor",
        desc: '+12 Armor, +50 Max HP, +10% Physical Resistance',
        effect: s => { s.armor += 12; s.maxHp += 50; s.hp = Math.min(s.maxHp, s.hp + 50); s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.10); },
      },
    ],
  },

  // ── Branch 7: Wisdom ───────────────────────────────────────
  {
    id: 'wis',
    label: 'Wisdom',
    color: '#52e0c8',
    angle: 5 * Math.PI / 4,
    smalls: [
      { icon: '📚', label: 'Study',          desc: '+0.5 XP/Kill',    effect: s => { s.xpPerKill = (s.xpPerKill ?? 1) + 0.5; } },
      { icon: '🔭', label: 'Curiosity',      desc: '+3% Magic Find',  effect: s => { s.magicFind = (s.magicFind ?? 0) + 0.03; } },
      { icon: '⚡', label: 'Focus',          desc: '-2% Cooldown',    effect: s => { s.cdMult *= 0.98; } },
      { icon: '📏', label: "Scholar's Eye",  desc: '+2% Range',       effect: s => { s.rangeMult *= 1.02; } },
    ],
    hub: {
      icon: '🎓',
      label: 'Scholar',
      desc: '+2 XP/Kill',
      effect: s => { s.xpPerKill = (s.xpPerKill ?? 1) + 2; },
    },
    notables: [
      {
        icon: '📖',
        label: 'Knowledge Seeker',
        desc: '+2 XP/Kill, +5% Speed',
        effect: s => { s.xpPerKill += 2; s.speedMult *= 1.05; },
      },
      {
        icon: '🏛️',
        label: 'Ancient Texts',
        desc: '+3 XP/Kill',
        effect: s => { s.xpPerKill += 3; },
      },
      {
        icon: '🧠',
        label: 'Enlightened',
        desc: '+2 XP/Kill, +10% Range, +10% Magic Find',
        effect: s => { s.xpPerKill += 2; s.rangeMult *= 1.10; s.magicFind = (s.magicFind ?? 0) + 0.10; },
      },
      {
        icon: '🎓',
        label: 'Master Student',
        desc: '+3 XP/Kill, +1 ATK',
        effect: s => { s.xpPerKill += 3; s.attackDamage += 1; },
      },
    ],
    keystones: [
      {
        icon: '📚',
        label: 'Transcendence',
        desc: '+7 XP/Kill, +20% Magic Find',
        effect: s => { s.xpPerKill += 7; s.magicFind = (s.magicFind ?? 0) + 0.20; },
      },
      {
        icon: '⚡',
        label: 'Experience Surge',
        desc: '+5 XP/Kill, +20% Speed',
        effect: s => { s.xpPerKill += 5; s.speedMult *= 1.20; },
      },
      {
        icon: '🌟',
        label: 'Grandmaster',
        desc: '+6 XP/Kill, +2 ATK',
        effect: s => { s.xpPerKill += 6; s.attackDamage += 2; },
      },
      {
        icon: '🧠',
        label: 'Sage Ascendant',
        desc: '+5 XP/Kill, -20% Cooldown',
        effect: s => { s.xpPerKill += 5; s.cdMult *= 0.80; },
      },
    ],
  },

  // ── Branch 7: Slaughter ────────────────────────────────────
  {
    id: 'slau',
    label: 'Slaughter',
    color: '#e08052',
    angle: 3 * Math.PI / 2,
    smalls: [
      { icon: '🩸', label: 'Kill Training', desc: '+0.3 ATK, -2% Cooldown', effect: s => { s.attackDamage += 0.3; s.cdMult *= 0.98; } },
      { icon: '🏃', label: 'Hunter Pace',   desc: '+3% Move Speed',          effect: s => { s.speedMult *= 1.03; } },
      { icon: '📏', label: 'Long Strike',   desc: '+2% Range',               effect: s => { s.rangeMult *= 1.02; } },
      { icon: '🎯', label: 'Blood Eye',     desc: '+0.5% Crit Chance',      effect: s => { s.critRate = Math.min(0.80, (s.critRate ?? 0.05) + 0.005); } },
    ],
    hub: {
      icon: '💀',
      label: 'Mass Slaughter',
      desc: '+2 ATK, -10% Cooldown',
      effect: s => { s.attackDamage += 2; s.cdMult *= 0.90; },
    },
    notables: [
      {
        icon: '🔥',
        label: 'Blood Frenzy',
        desc: '+2 ATK, +2 XP/Kill',
        effect: s => { s.attackDamage += 2; s.xpPerKill += 2; },
      },
      {
        icon: '🗡️',
        label: "Hunter's Mark",
        desc: '+3 ATK, +15% Range',
        effect: s => { s.attackDamage += 3; s.rangeMult *= 1.15; },
      },
      {
        icon: '⚡',
        label: 'Slayer',
        desc: '+2 ATK, +15% Speed',
        effect: s => { s.attackDamage += 2; s.speedMult *= 1.15; },
      },
      {
        icon: '💀',
        label: "Reaper's Touch",
        desc: '+3 ATK, -15% Cooldown',
        effect: s => { s.attackDamage += 3; s.cdMult *= 0.85; },
      },
    ],
    keystones: [
      {
        icon: '💀',
        label: 'Bloodbath',
        desc: '+6 ATK, -20% Cooldown',
        effect: s => { s.attackDamage += 6; s.cdMult *= 0.80; },
      },
      {
        icon: '🔥',
        label: 'Death Sentence',
        desc: '+5 ATK, +20% Range',
        effect: s => { s.attackDamage += 5; s.rangeMult *= 1.20; },
      },
      {
        icon: '⚡',
        label: 'Massacre',
        desc: '+4 ATK, +30% Speed, +1 XP/Kill',
        effect: s => { s.attackDamage += 4; s.speedMult *= 1.30; s.xpPerKill += 1; },
      },
      {
        icon: '🩸',
        label: 'Executioner',
        desc: '+8 Attack Damage',
        effect: s => { s.attackDamage += 8; },
      },
    ],
  },

  // ── Branch 8: Dominion ─────────────────────────────────────
  {
    id: 'dom',
    label: 'Dominion',
    color: '#9060c8',
    angle: 7 * Math.PI / 4,
    smalls: [
      { icon: '🌀', label: 'Aura Training',    desc: '-4% Enemy Speed',  effect: s => { s.enemySlowMult = (s.enemySlowMult ?? 1) * 0.96; } },
      { icon: '📏', label: 'Commanding Reach', desc: '+2% Range',         effect: s => { s.rangeMult *= 1.02; } },
      { icon: '⏱️', label: 'Aura Tempo',       desc: '-1.5% Cooldown',   effect: s => { s.cdMult *= 0.985; } },
      { icon: '🏃', label: 'Dominant Stride',  desc: '+2% Move Speed',   effect: s => { s.speedMult *= 1.02; } },
    ],
    hub: {
      icon: '⚓',
      label: 'Heavy Aura',
      desc: '-15% Enemy Speed',
      effect: s => { s.enemySlowMult = (s.enemySlowMult ?? 1) * 0.85; },
    },
    notables: [
      {
        icon: '🌊',
        label: 'Gravity',
        desc: '-15% Enemy Speed, +1 XP/Kill',
        effect: s => { s.enemySlowMult *= 0.85; s.xpPerKill += 1; },
      },
      {
        icon: '🌀',
        label: 'Quagmire',
        desc: '-20% Enemy Speed',
        effect: s => { s.enemySlowMult *= 0.80; },
      },
      {
        icon: '⚓',
        label: 'Sluggish World',
        desc: '-15% Enemy Speed, +15% Range',
        effect: s => { s.enemySlowMult *= 0.85; s.rangeMult *= 1.15; },
      },
      {
        icon: '🌑',
        label: 'Bog Down',
        desc: '-25% Enemy Speed, +1 ATK',
        effect: s => { s.enemySlowMult *= 0.75; s.attackDamage += 1; },
      },
    ],
    keystones: [
      {
        icon: '⚓',
        label: 'Supreme Control',
        desc: '-45% Enemy Speed',
        effect: s => { s.enemySlowMult *= 0.55; },
      },
      {
        icon: '🌊',
        label: 'Frozen World',
        desc: '-40% Enemy Speed, +3 ATK',
        effect: s => { s.enemySlowMult *= 0.60; s.attackDamage += 3; },
      },
      {
        icon: '🌑',
        label: 'Petrify',
        desc: '-35% Enemy Speed, -25% Cooldown',
        effect: s => { s.enemySlowMult *= 0.65; s.cdMult *= 0.75; },
      },
      {
        icon: '⚡',
        label: "Warlord's Presence",
        desc: '-40% Enemy Speed, +20% Speed',
        effect: s => { s.enemySlowMult *= 0.60; s.speedMult *= 1.20; },
      },
    ],
  },

  // ── Branch 11: Guard (Block) ────────────────────────────────
  // Angle 67.5° — between Swiftness (45°) and Sorcery (90°)
  {
    id: 'guard',
    label: 'Guard',
    color: '#6688ff',
    angle: 3 * Math.PI / 8,
    smalls: [
      { icon: '🛡️', label: 'Block Training', desc: '+50ms Block Duration',     effect: s => { s.blockImmuneMsBonus = (s.blockImmuneMsBonus ?? 0) + 50; } },
      { icon: '⚒️', label: 'Shield Weight',  desc: '+1 Armor',                  effect: s => { s.armor = (s.armor ?? 0) + 1; } },
      { icon: '❤️', label: 'Guard Stamina',  desc: '+5 Max HP',                 effect: s => { s.maxHp = (s.maxHp ?? 100) + 5; s.hp = Math.min(s.maxHp, s.hp + 5); } },
      { icon: '⚡', label: 'Block React',    desc: '-20ms Block Cooldown',      effect: s => { s.blockCooldownReduction = Math.min(480, (s.blockCooldownReduction ?? 0) + 20); } },
    ],
    hub: {
      icon: '🛡️',
      label: 'Iron Guard',
      desc: '+300ms Block Duration, -60ms Block Cooldown',
      effect: s => {
        s.blockImmuneMsBonus    = (s.blockImmuneMsBonus    ?? 0) + 300;
        s.blockCooldownReduction = Math.min(480, (s.blockCooldownReduction ?? 0) + 60);
      },
    },
    notables: [
      {
        icon: '🏰',
        label: 'Shield Wall',
        desc: '+200ms Block Duration, +8 Armor',
        effect: s => { s.blockImmuneMsBonus = (s.blockImmuneMsBonus ?? 0) + 200; s.armor = (s.armor ?? 0) + 8; },
      },
      {
        icon: '⚡',
        label: 'Perfect Guard',
        desc: '+100ms Block Duration, -80ms Block Cooldown',
        effect: s => {
          s.blockImmuneMsBonus    = (s.blockImmuneMsBonus    ?? 0) + 100;
          s.blockCooldownReduction = Math.min(480, (s.blockCooldownReduction ?? 0) + 80);
        },
      },
      {
        icon: '💠',
        label: 'Reactive Guard',
        desc: '+150ms Block Duration, +5% Physical Resistance',
        effect: s => { s.blockImmuneMsBonus = (s.blockImmuneMsBonus ?? 0) + 150; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.05); },
      },
      {
        icon: '❤️',
        label: 'Ironblock',
        desc: '+200ms Block Duration, +20 Max HP',
        effect: s => { s.blockImmuneMsBonus = (s.blockImmuneMsBonus ?? 0) + 200; s.maxHp += 20; s.hp = Math.min(s.maxHp, s.hp + 20); },
      },
    ],
    keystones: [
      {
        icon: '⚔️',
        label: 'Aegis',
        desc: '+600ms Block Duration, +15 Armor',
        effect: s => { s.blockImmuneMsBonus = (s.blockImmuneMsBonus ?? 0) + 600; s.armor = (s.armor ?? 0) + 15; },
      },
      {
        icon: '🛡️',
        label: 'Bastion',
        desc: '+400ms Block Duration, -200ms Block Cooldown',
        effect: s => {
          s.blockImmuneMsBonus    = (s.blockImmuneMsBonus    ?? 0) + 400;
          s.blockCooldownReduction = Math.min(480, (s.blockCooldownReduction ?? 0) + 200);
        },
      },
      {
        icon: '💨',
        label: "Guardian's Oath",
        desc: '+500ms Block Duration, +5% Dodge Chance',
        effect: s => { s.blockImmuneMsBonus = (s.blockImmuneMsBonus ?? 0) + 500; s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.05); },
      },
      {
        icon: '🏰',
        label: 'Unbreakable Will',
        desc: '+1000ms Block Duration',
        effect: s => { s.blockImmuneMsBonus = (s.blockImmuneMsBonus ?? 0) + 1000; },
      },
    ],
  },

  // ── Branch 12: Evasion (Dodge) ──────────────────────────────
  // Angle 157.5° — between Fortune (135°) and Vitality (180°)
  {
    id: 'eva',
    label: 'Evasion',
    color: '#44ddaa',
    angle: 7 * Math.PI / 8,
    smalls: [
      { icon: '💨', label: 'Evasion Training', desc: '+0.2% Dodge Chance',        effect: s => { s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.002); } },
      { icon: '🏃', label: 'Light Step',        desc: '+3% Move Speed',            effect: s => { s.speedMult *= 1.03; } },
      { icon: '❤️', label: 'Evasive Body',      desc: '+5 Max HP',                 effect: s => { s.maxHp = (s.maxHp ?? 100) + 5; s.hp = Math.min(s.maxHp, s.hp + 5); } },
      { icon: '🩺', label: 'Phase Body',        desc: '+1% Physical Resistance',  effect: s => { s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.01); } },
    ],
    hub: {
      icon: '🌀',
      label: 'Phantom Footing',
      desc: '+2% Dodge Chance, +5% Speed',
      effect: s => { s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.02); s.speedMult *= 1.05; },
    },
    notables: [
      {
        icon: '🌫️',
        label: 'Shadow Step',
        desc: '+1.5% Dodge Chance, +5% Speed',
        effect: s => { s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.015); s.speedMult *= 1.05; },
      },
      {
        icon: '💫',
        label: 'Phantom Move',
        desc: '+1% Dodge Chance, +8% Speed',
        effect: s => { s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.01); s.speedMult *= 1.08; },
      },
      {
        icon: '🌀',
        label: 'Ghost Form',
        desc: '+2% Dodge Chance, +5% Physical Resistance',
        effect: s => { s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.02); s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.05); },
      },
      {
        icon: '🍃',
        label: 'Veil Walk',
        desc: '+1.5% Dodge Chance, +20 Max HP',
        effect: s => { s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.015); s.maxHp += 20; s.hp = Math.min(s.maxHp, s.hp + 20); },
      },
    ],
    keystones: [
      {
        icon: '💨',
        label: 'Untouchable',
        desc: '+8% Dodge Chance',
        effect: s => { s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.08); },
      },
      {
        icon: '💃',
        label: 'Phase Dancer',
        desc: '+5% Dodge Chance, +15% Speed',
        effect: s => { s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.05); s.speedMult *= 1.15; },
      },
      {
        icon: '🌫️',
        label: 'Ethereal Form',
        desc: '+6% Dodge Chance, +5% Physical Resistance',
        effect: s => { s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.06); s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.05); },
      },
      {
        icon: '🌟',
        label: 'Mirage',
        desc: '+5% Dodge Chance, +10% Speed, +30 Max HP',
        effect: s => { s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.05); s.speedMult *= 1.10; s.maxHp += 30; s.hp = Math.min(s.maxHp, s.hp + 30); },
      },
    ],
  },

  // ── Branch 13: Endurance (HP) ───────────────────────────────
  // Angle 247.5° — between Wisdom (225°) and Slaughter (270°)
  {
    id: 'end',
    label: 'Endurance',
    color: '#ff8855',
    angle: 11 * Math.PI / 8,
    smalls: [
      { icon: '❤️', label: 'Life Training',   desc: '+5 Max HP',               effect: s => { s.maxHp = (s.maxHp ?? 100) + 5; s.hp = Math.min(s.maxHp, s.hp + 5); } },
      { icon: '⚒️', label: 'Body Forge',      desc: '+1 Armor',                effect: s => { s.armor = (s.armor ?? 0) + 1; } },
      { icon: '🩺', label: 'Resist Training', desc: '+2% Physical Resistance', effect: s => { s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.02); } },
      { icon: '🛡️', label: 'Pain Tolerance',  desc: '-20ms Hit Cooldown',      effect: s => { s.hitCooldown = Math.max(600, (s.hitCooldown ?? 1500) - 20); } },
    ],
    hub: {
      icon: '💪',
      label: 'Iron Body',
      desc: '+30 Max HP, +3 Armor',
      effect: s => { s.maxHp = (s.maxHp ?? 100) + 30; s.hp = Math.min(s.maxHp, s.hp + 30); s.armor = (s.armor ?? 0) + 3; },
    },
    notables: [
      {
        icon: '🩸',
        label: 'Iron Flesh',
        desc: '+20 Max HP, +4 Armor',
        effect: s => { s.maxHp += 20; s.hp = Math.min(s.maxHp, s.hp + 20); s.armor = (s.armor ?? 0) + 4; },
      },
      {
        icon: '⚡',
        label: 'Vitality Surge',
        desc: '+30 Max HP, +5% Speed',
        effect: s => { s.maxHp += 30; s.hp = Math.min(s.maxHp, s.hp + 30); s.speedMult *= 1.05; },
      },
      {
        icon: '🏰',
        label: 'Bulwark',
        desc: '+20 Max HP, +5% Physical Resistance',
        effect: s => { s.maxHp += 20; s.hp = Math.min(s.maxHp, s.hp + 20); s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.05); },
      },
      {
        icon: '❤️',
        label: 'Life Force',
        desc: '+40 Max HP',
        effect: s => { s.maxHp += 40; s.hp = Math.min(s.maxHp, s.hp + 40); },
      },
    ],
    keystones: [
      {
        icon: '🗿',
        label: "Giant's Blood",
        desc: '+80 Max HP, +5 Armor',
        effect: s => { s.maxHp += 80; s.hp = Math.min(s.maxHp, s.hp + 80); s.armor = (s.armor ?? 0) + 5; },
      },
      {
        icon: '💥',
        label: 'Colossus',
        desc: '+100 Max HP',
        effect: s => { s.maxHp += 100; s.hp = Math.min(s.maxHp, s.hp + 100); },
      },
      {
        icon: '🛡️',
        label: 'Ironwall',
        desc: '+70 Max HP, +5% Physical Resistance',
        effect: s => { s.maxHp += 70; s.hp = Math.min(s.maxHp, s.hp + 70); s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.05); },
      },
      {
        icon: '💨',
        label: 'Undying',
        desc: '+80 Max HP, +3% Dodge Chance',
        effect: s => { s.maxHp += 80; s.hp = Math.min(s.maxHp, s.hp + 80); s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.03); },
      },
    ],
  },
];

// ── Build all 13 branches ─────────────────────────────────────
branches.forEach(branch => buildBranch(branch));

// ── Build 52 bridge nodes (4 per adjacent pair × 13 branches) ────────────────
// Branches sorted by angle (clockwise) so bridges connect truly adjacent hubs.
const BRANCH_RING_ORDER = ['war','crit','swift','guard','sorc','fort','eva','vit','armor','wis','end','slau','dom'];
const _branchById = Object.fromEntries(branches.map(b => [b.id, b]));
for (let i = 0; i < BRANCH_RING_ORDER.length; i++) {
  const bA = _branchById[BRANCH_RING_ORDER[i]];
  const bB = _branchById[BRANCH_RING_ORDER[(i + 1) % BRANCH_RING_ORDER.length]];
  const hubA = allNodes.find(n => n.id === `${bA.id}_hub`);
  const hubB = allNodes.find(n => n.id === `${bB.id}_hub`);
  const bridgeIds = [0, 1, 2, 3].map(k => `bridge_${bA.id}_${bB.id}_${k}`);

  [0.2, 0.4, 0.6, 0.8].forEach((t, k) => {
    allNodes.push({
      id: bridgeIds[k],
      x: Math.round(hubA.x + t * (hubB.x - hubA.x)),
      y: Math.round(hubA.y + t * (hubB.y - hubA.y)),
      type: 'small',
      branch: bA.id,
      color: bA.color,
      connections: [],
      ...(Array.isArray(bA.smalls) ? bA.smalls[k % bA.smalls.length] : bA.small),
    });
  });

  addEdge(`${bA.id}_hub`, bridgeIds[0]);
  addEdge(bridgeIds[0], bridgeIds[1]);
  addEdge(bridgeIds[1], bridgeIds[2]);
  addEdge(bridgeIds[2], bridgeIds[3]);
  addEdge(bridgeIds[3], `${bB.id}_hub`);
}


// ── Long cross-branch bridges ──────────────────────────────────
// 3 arcing notable paths connecting non-adjacent branch hubs.
// Swift (45°) ↔ Fort (135°) — 90° arc, sharing "Wanderer's Lore" notable
(function buildLongBridge(bA, bB, defs) {
  const hA = allNodes.find(n => n.id === `${bA}_hub`);
  const hB = allNodes.find(n => n.id === `${bB}_hub`);
  const ids = defs.map((_, k) => `xbridge_${bA}_${bB}_${k}`);
  const ts  = [0.25, 0.50, 0.75];
  defs.forEach((def, k) => {
    allNodes.push({
      id: ids[k],
      x: Math.round(hA.x + ts[k] * (hB.x - hA.x)),
      y: Math.round(hA.y + ts[k] * (hB.y - hA.y)),
      type: k === 1 ? 'notable' : 'small',
      branch: bA,
      color: k === 1 ? '#d4a8f0' : '#a0b8cc',
      connections: [],
      ...def,
    });
  });
  addEdge(`${bA}_hub`, ids[0]);
  addEdge(ids[0], ids[1]);
  addEdge(ids[1], ids[2]);
  addEdge(ids[2], `${bB}_hub`);
}('swift', 'fort', [
  { icon: '🌿', label: 'Wanderer', desc: '+5% Speed', effect: s => { s.speedMult *= 1.05; } },
  { icon: '🗺️', label: "Wanderer's Lore", desc: '+10% Speed, +15% Magic Find', effect: s => { s.speedMult *= 1.10; s.magicFind = (s.magicFind ?? 0) + 0.15; } },
  { icon: '🌿', label: 'Road-Worn', desc: '+6% Gold Find', effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 0.06; } },
]));

// Sorc (90°) ↔ Vit (180°) — "Arcane Ward" notable path
(function buildLongBridge(bA, bB, defs) {
  const hA = allNodes.find(n => n.id === `${bA}_hub`);
  const hB = allNodes.find(n => n.id === `${bB}_hub`);
  const ids = defs.map((_, k) => `xbridge_${bA}_${bB}_${k}`);
  const ts  = [0.25, 0.50, 0.75];
  defs.forEach((def, k) => {
    allNodes.push({
      id: ids[k],
      x: Math.round(hA.x + ts[k] * (hB.x - hA.x)),
      y: Math.round(hA.y + ts[k] * (hB.y - hA.y)),
      type: k === 1 ? 'notable' : 'small',
      branch: bA,
      color: k === 1 ? '#d4a8f0' : '#a0b8cc',
      connections: [],
      ...def,
    });
  });
  addEdge(`${bA}_hub`, ids[0]);
  addEdge(ids[0], ids[1]);
  addEdge(ids[1], ids[2]);
  addEdge(ids[2], `${bB}_hub`);
}('sorc', 'vit', [
  { icon: '🔮', label: 'Mystic Barrier', desc: '-4% Cooldown', effect: s => { s.cdMult *= 0.96; } },
  { icon: '🌀', label: 'Arcane Ward', desc: '-10% Cooldown, +5% Physical Resistance', effect: s => { s.cdMult *= 0.90; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.05); } },
  { icon: '🔮', label: 'Warded Core', desc: '+15 Max HP', effect: s => { s.maxHp += 15; s.hp = Math.min(s.maxHp, s.hp + 15); } },
]));

// Armor (202.5°) ↔ Slau (270°) — "Iron Executioner" notable path
(function buildLongBridge(bA, bB, defs) {
  const hA = allNodes.find(n => n.id === `${bA}_hub`);
  const hB = allNodes.find(n => n.id === `${bB}_hub`);
  const ids = defs.map((_, k) => `xbridge_${bA}_${bB}_${k}`);
  const ts  = [0.25, 0.50, 0.75];
  defs.forEach((def, k) => {
    allNodes.push({
      id: ids[k],
      x: Math.round(hA.x + ts[k] * (hB.x - hA.x)),
      y: Math.round(hA.y + ts[k] * (hB.y - hA.y)),
      type: k === 1 ? 'notable' : 'small',
      branch: bA,
      color: k === 1 ? '#d4a8f0' : '#a0b8cc',
      connections: [],
      ...def,
    });
  });
  addEdge(`${bA}_hub`, ids[0]);
  addEdge(ids[0], ids[1]);
  addEdge(ids[1], ids[2]);
  addEdge(ids[2], `${bB}_hub`);
}('armor', 'slau', [
  { icon: '⚒️', label: 'Heavy Striker', desc: '+1 ATK, +1 Armor', effect: s => { s.attackDamage += 1; s.armor += 1; } },
  { icon: '💀', label: 'Iron Executioner', desc: '+3 ATK, +4 Armor, +5% Physical Resistance', effect: s => { s.attackDamage += 3; s.armor += 4; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.05); } },
  { icon: '⚒️', label: 'Killing Plate', desc: '+1 ATK, -5% Cooldown', effect: s => { s.attackDamage += 1; s.cdMult *= 0.95; } },
]));

// ── Secondary hub notables ─────────────────────────────────────
// Each adds an inner notable per branch that all sub-branches can pivot through,
// creating extra path choices at the mid-ring.
[
  { branch: 'war',   da: 0,      r: 560, def: { icon: '⚔️', label: 'Veteran', desc: '+1.5 ATK, +5% Range',       effect: s => { s.attackDamage += 1.5; s.rangeMult *= 1.05; } } },
  { branch: 'crit',  da: 0,      r: 560, def: { icon: '🎯', label: "Hawk's Eye", desc: '+4% Crit Chance, +0.15× Crit Dmg', effect: s => { s.critRate = Math.min(0.80, s.critRate + 0.04); s.critDamage += 0.15; } } },
  { branch: 'swift', da: 0,      r: 560, def: { icon: '💨', label: 'Gust', desc: '+8% Speed, +3% Range',          effect: s => { s.speedMult *= 1.08; s.rangeMult *= 1.03; } } },
  { branch: 'sorc',  da: 0,      r: 560, def: { icon: '🌟', label: 'Spell Cache', desc: '-8% Cooldown',           effect: s => { s.cdMult *= 0.92; } } },
  { branch: 'fort',  da: 0,      r: 560, def: { icon: '💰', label: 'Appraiser', desc: '+15% Gold Find, +8% Magic Find', effect: s => { s.goldFindBonus = (s.goldFindBonus ?? 0) + 0.15; s.magicFind = (s.magicFind ?? 0) + 0.08; } } },
  { branch: 'vit',   da: 0,      r: 560, def: { icon: '❤️', label: 'Life Spring', desc: '+25 Max HP',              effect: s => { s.maxHp += 25; s.hp = Math.min(s.maxHp, s.hp + 25); } } },
  { branch: 'armor', da: 0,      r: 560, def: { icon: '🛡️', label: 'Iron Core', desc: '+3 Armor, +4% Physical Resistance', effect: s => { s.armor += 3; s.resistance = Math.min(0.75, (s.resistance ?? 0) + 0.04); } } },
  { branch: 'wis',   da: 0,      r: 560, def: { icon: '📖', label: 'Deep Study', desc: '+1.5 XP/Kill, +5% Magic Find', effect: s => { s.xpPerKill += 1.5; s.magicFind = (s.magicFind ?? 0) + 0.05; } } },
  { branch: 'slau',  da: 0,      r: 560, def: { icon: '🩸', label: 'Blood Rite', desc: '+1.5 ATK, -5% Cooldown', effect: s => { s.attackDamage += 1.5; s.cdMult *= 0.95; } } },
  { branch: 'dom',   da: 0,      r: 560, def: { icon: '🌀', label: 'Weight', desc: '-8% Enemy Speed, +3% Range', effect: s => { s.enemySlowMult *= 0.92; s.rangeMult *= 1.03; } } },
  // New branches
  { branch: 'guard', da: 0,      r: 560, def: { icon: '🛡️', label: 'Guard Mastery',     desc: '+150ms Block Duration, -30ms Block Cooldown', effect: s => { s.blockImmuneMsBonus = (s.blockImmuneMsBonus ?? 0) + 150; s.blockCooldownReduction = Math.min(480, (s.blockCooldownReduction ?? 0) + 30); } } },
  { branch: 'eva',   da: 0,      r: 560, def: { icon: '💨', label: 'Evasion Mastery',    desc: '+1.5% Dodge Chance, +5% Speed',               effect: s => { s.dodgeChance = Math.min(0.75, (s.dodgeChance ?? 0) + 0.015); s.speedMult *= 1.05; } } },
  { branch: 'end',   da: 0,      r: 560, def: { icon: '❤️', label: 'Endurance Mastery',  desc: '+20 Max HP, +2 Armor',                         effect: s => { s.maxHp = (s.maxHp ?? 100) + 20; s.hp = Math.min(s.maxHp, s.hp + 20); s.armor = (s.armor ?? 0) + 2; } } },
].forEach(({ branch, da, r, def }) => {
  const branchDef = branches.find(b => b.id === branch);
  const angle     = branchDef.angle + da;
  const hubId     = `${branch}_hub`;
  const secId     = `${branch}_sec_hub`;
  allNodes.push({
    id: secId,
    x: Math.round(Math.cos(angle) * r * NODE_SCALE),
    y: Math.round(Math.sin(angle) * r * NODE_SCALE),
    type: 'notable',
    branch,
    color: branchDef.color,
    connections: [],
    ...def,
  });
  // Connect to hub and to each sub-branch's first node
  addEdge(hubId, secId);
  [0, 1, 2, 3].forEach(s => addEdge(secId, `${branch}_p${s}_0`));
});

// ── Exports ───────────────────────────────────────────────────

export const TALENT_NODES = allNodes;

export const TALENT_EDGES = allEdges;

export const TALENT_NODE_MAP = new Map(TALENT_NODES.map(n => [n.id, n]));

/**
 * Returns the gold cost to activate the next talent node.
 * @param {number} totalActivated — number of currently activated nodes
 * @returns {number}
 */
/**
 * Returns the talent-point cost for a node based on its type.
 * Points are earned by levelling up during runs (1 point per level-up, persisted).
 *   origin   → 0  (already active; always free)
 *   small    → 1
 *   notable  → 2
 *   keystone → 4
 */
export function getNodeCost(nodeOrType) {
  const type = typeof nodeOrType === 'string' ? nodeOrType : nodeOrType?.type;
  switch (type) {
    case 'origin':   return 0;
    case 'notable':  return 2;
    case 'keystone': return 4;
    default:         return 1; // 'small'
  }
}

/**
 * Applies all activated talent effects to the given state object.
 * Call this after resetting state to base values each frame / on load.
 * NOTE: effect functions are NOT serialisable — never store nodes in localStorage.
 *
 * @param {object} state — mutable game state
 * @param {string[]} activatedIds — array of activated node IDs
 */
export function applyTalents(state, activatedIds) {
  if (!activatedIds || activatedIds.length === 0) return;
  const nodeMap = new Map(TALENT_NODES.map(n => [n.id, n]));
  for (const id of activatedIds) {
    const node = nodeMap.get(id);
    if (node?.effect) node.effect(state);
  }
}
